/**
 * A deferred promise implementation that exposes resolve and reject handlers. Useful for creating
 * promises that can be resolved or rejected from outside their executor.
 *
 * @warning Do NOT call resolve or reject on the promise itself (e.g.,
 * `deferred.promise.then(...).resolve(value)`). Instead, use the resolve and reject methods exposed
 * on the Deferred instance (e.g., `deferred.resolve(value)`). The promise's native resolve/reject
 * will bypass the guard checks that prevent multiple resolutions/rejections. Only use the methods
 * on the Deferred instance to ensure proper state management and guard semantics.
 */
export class Deferred<T = void> {
  promise: Promise<T>;
  resolve!: (value: T | PromiseLike<T>) => void;
  reject!: (reason?: unknown) => void;

  private _resolved = false;
  private _rejected = false;

  constructor() {
    this.promise = new Promise<T>((resolve, reject) => {
      this.resolve = (value: T | PromiseLike<T>) => {
        if (!this._resolved && !this._rejected) {
          this._resolved = true;
          resolve(value);
        }
      };
      this.reject = (reason?: unknown) => {
        if (!this._resolved && !this._rejected) {
          this._rejected = true;
          reject(reason);
        }
      };
    });
    Object.freeze(this.promise);
  }

  /**
   * Returns true if the promise has been resolved.
   */
  get resolved(): boolean {
    return this._resolved;
  }

  /**
   * Returns true if the promise has been rejected.
   */
  get rejected(): boolean {
    return this._rejected;
  }

  /**
   * Returns true if the promise is still pending (neither resolved nor rejected).
   */
  get pending(): boolean {
    return !this._resolved && !this._rejected;
  }
}

export default Deferred;
